import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { safeNavigate } from '../utils/navigationGuard';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Building2, LogOut, User, Settings, Users, Shield } from 'lucide-react';
import Logo from '@/components/Logo';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getRoleBadge = (role) => {
  const badges = { owner: 'bg-purple-100 text-purple-800', admin: 'bg-blue-100 text-blue-800', user: 'bg-green-100 text-green-800' };
  const labels = { owner: 'Owner', admin: 'Admin', user: 'User' };
  return <span className={`px-3 py-1 rounded-full text-xs font-medium ${badges[role] || 'bg-gray-100 text-gray-800'}`}>{labels[role] || role}</span>;
};

const Header = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState({ firstName: '', lastName: '', email: '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [companyData, setCompanyData] = useState({
    companyName: '', companyType: '', description: '',
    headquartersAddress: { street: '', state: '', city: '', zipCode: '' },
    email: '', website: '', phone: '', fax: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (user) {
      setProfileData({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', currentPassword: '', newPassword: '', confirmNewPassword: '' });
    }
  }, [user]);

  const loadStates = async () => { try { const res = await axios.get(`${API}/states`); setStates(res.data.states || []); } catch (e) {} };
  const loadCities = async (stateCode) => { try { const res = await axios.get(`${API}/cities/${stateCode}`); setCities(res.data.cities || []); } catch (e) {} };

  const loadCompanyData = async () => {
    if (!user?.companyId) return;
    try {
      const res = await axios.get(`${API}/users/company`);
      const c = res.data;
      setCompanyData({
        companyName: c.companyName || '', companyType: c.companyType || '', description: c.description || '',
        headquartersAddress: c.headquartersAddress || { street: '', state: '', city: '', zipCode: '' },
        email: c.email || '', website: c.website || '', phone: c.phone || '', fax: c.fax || ''
      });
      if (c.logoCloudinary?.urls?.medium) setLogoPreview(c.logoCloudinary.urls.medium);
      else if (c.logo) setLogoPreview(`${BACKEND_URL}${c.logo}`);
      else setLogoPreview(null);
      if (c.headquartersAddress?.state) loadCities(c.headquartersAddress.state);
    } catch (e) {}
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (profileData.newPassword && profileData.newPassword !== profileData.confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      const updateData = { firstName: profileData.firstName, lastName: profileData.lastName, email: profileData.email };
      if (profileData.currentPassword && profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }
      await axios.put(`${API}/users/profile`, updateData);
      toast.success('Profile updated successfully');
      setShowProfileDialog(false);
      window.location.reload();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update profile'); }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        await axios.post(`${API}/upload/company-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      await axios.put(`${API}/users/company`, companyData);
      toast.success('Company settings updated successfully');
      setShowCompanyDialog(false);
      setLogoFile(null);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update company settings'); }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <header className="border-b border-slate-200 sticky top-0 z-30" style={{ background: '#FF6600' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-6 cursor-pointer" onClick={() => safeNavigate(navigate, '/dashboard')}>
              <Logo size="md" showText={true} textColor="white" />
            </div>
            <div className="flex items-center gap-4">
              {getRoleBadge(user?.role)}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="bg-white text-orange-600 hover:bg-white hover:bg-opacity-90" size="sm" data-testid="my-account-menu-btn">
                    <User className="h-4 w-4 mr-2" />
                    {user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setProfileData({ firstName: user?.firstName || '', lastName: user?.lastName || '', email: user?.email || '', currentPassword: '', newPassword: '', confirmNewPassword: '' }); setShowProfileDialog(true); }} data-testid="profile-settings-btn">
                    <Settings className="h-4 w-4 mr-2" />
                    Profile Settings
                  </DropdownMenuItem>
                  {user?.companyId && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => { loadStates(); loadCompanyData(); setShowCompanyDialog(true); }} data-testid="company-settings-btn">
                        <Building2 className="h-4 w-4 mr-2" />
                        Company Settings
                      </DropdownMenuItem>
                      {(user?.role === 'owner' || user?.role === 'admin') && !user?.isSuperAdmin && (
                        <DropdownMenuItem onClick={() => safeNavigate(navigate, '/company/users')} data-testid="manage-team-btn">
                          <Users className="h-4 w-4 mr-2" />
                          Manage Team
                        </DropdownMenuItem>
                      )}
                    </>
                  )}
                  {user?.role === 'admin' && user?.email === 'admin@specard.net' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => safeNavigate(navigate, '/admin')} data-testid="admin-console-btn">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin Console
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => safeNavigate(navigate, '/auth-activity')} data-testid="auth-activity-btn">
                    Auth Activity
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => logout(navigate)} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Settings Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profile Settings</DialogTitle>
            <DialogDescription>Update your account information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateProfile} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>First Name *</Label><Input value={profileData.firstName} onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })} required data-testid="profile-firstname-input" /></div>
              <div className="space-y-2"><Label>Last Name *</Label><Input value={profileData.lastName} onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })} required data-testid="profile-lastname-input" /></div>
            </div>
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={profileData.email} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} required data-testid="profile-email-input" /></div>
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Change Password (Optional)</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <PasswordInput value={profileData.currentPassword} onChange={(e) => setProfileData({ ...profileData, currentPassword: e.target.value })} placeholder="Leave blank to keep current" data-testid="profile-current-password" />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <PasswordInput value={profileData.newPassword} onChange={(e) => setProfileData({ ...profileData, newPassword: e.target.value })} placeholder="Leave blank to keep current" data-testid="profile-new-password" />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <PasswordInput value={profileData.confirmNewPassword} onChange={(e) => setProfileData({ ...profileData, confirmNewPassword: e.target.value })} placeholder="Re-enter new password" data-testid="profile-confirm-new-password" />
                  {profileData.newPassword && profileData.confirmNewPassword && profileData.newPassword !== profileData.confirmNewPassword && (
                    <p className="text-sm text-red-500" data-testid="profile-password-mismatch-error">Passwords do not match</p>
                  )}
                </div>
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="update-profile-button">Update Profile</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Company Settings Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Company Settings</DialogTitle>
            <DialogDescription>Update your company information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateCompany} className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Company Name *</Label><Input required value={companyData.companyName} onChange={(e) => setCompanyData({ ...companyData, companyName: e.target.value })} /></div>
              <div className="space-y-2"><Label>Company Type *</Label>
                <select required value={companyData.companyType} onChange={(e) => setCompanyData({ ...companyData, companyType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500">
                  <option value="">Select type</option><option value="General Contractor">General Contractor</option><option value="Sub Contractor">Sub Contractor</option>
                </select>
              </div>
            </div>
            <div className="space-y-2"><Label>Description</Label><textarea rows={3} value={companyData.description} onChange={(e) => setCompanyData({ ...companyData, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" /></div>
            <div className="space-y-2"><Label>Company Logo</Label>
              <div className="flex items-center gap-4">
                <div className="flex-1"><Input type="file" accept="image/*" onChange={handleLogoChange} /></div>
                {logoPreview && <div className="w-32 h-32 border-2 border-gray-200 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50"><img src={logoPreview} alt="Logo" className="w-full h-full object-contain" /></div>}
              </div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-700 mb-4">Headquarters Address</h4>
              <div className="space-y-4">
                <Input placeholder="Street Address" value={companyData.headquartersAddress?.street || ''} onChange={(e) => setCompanyData({ ...companyData, headquartersAddress: { ...companyData.headquartersAddress, street: e.target.value } })} />
                <div className="grid grid-cols-2 gap-4">
                  <select value={companyData.headquartersAddress?.state || ''} onChange={(e) => { const state = e.target.value; setCompanyData({ ...companyData, headquartersAddress: { ...companyData.headquartersAddress, state, city: '' } }); if (state) loadCities(state); }} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="">Select state</option>{states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                  <select value={companyData.headquartersAddress?.city || ''} onChange={(e) => setCompanyData({ ...companyData, headquartersAddress: { ...companyData.headquartersAddress, city: e.target.value } })} disabled={!companyData.headquartersAddress?.state} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100">
                    <option value="">Select city</option>{cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <Input placeholder="ZIP Code" value={companyData.headquartersAddress?.zipCode || ''} onChange={(e) => setCompanyData({ ...companyData, headquartersAddress: { ...companyData.headquartersAddress, zipCode: e.target.value } })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input type="email" value={companyData.email} onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Website</Label><Input type="url" value={companyData.website} onChange={(e) => setCompanyData({ ...companyData, website: e.target.value })} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input type="tel" value={companyData.phone} onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })} /></div>
              <div className="space-y-2"><Label>Fax</Label><Input type="tel" value={companyData.fax} onChange={(e) => setCompanyData({ ...companyData, fax: e.target.value })} /></div>
            </div>
            <div className="flex gap-4 pt-4 border-t">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCompanyDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1">Update Company Settings</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
